<template>
  <div>
    <!-- 顶部下滑弹窗 -->
    <transition name="popup-top">
      <div class="popup-mask" v-show="show" @click.self="close">
        <div class="popup-panel">
          <!-- 标题栏 -->
          <div class="header">
            <span class="title">{{ $i18n('rechargeCenter', '充值中心') }}</span>
            <span class="close-bt" @click="close">✕</span>
          </div>

          <!-- 充值选项：4列紧凑网格 -->
          <div class="recharge-grid">
            <div
                v-for="(item, index) in rechargeList"
                :key="item.id"
                class="recharge-item"
                :class="{ active: activeIndex === index }"
                @click="selectItem(index)"
            >
              <div class="tag" v-if="item.tag">{{ $i18n(item.tag, item.tag) }}</div>
              <div class="amount">¥{{ item.value.price }}</div>
              <div class="bonus" v-if="item.value.give_price > 0">
                +{{ item.value.give_price }}{{ $i18n('coins', '积分') }}
              </div>
            </div>
          </div>

          <!-- 底部操作：协议 + 按钮，都在面板内 -->
          <div class="action-row">
            <div class="agreement" @click="toggleAgreement">
              <span class="checkbox" :class="{ checked: isAgreed }">
                <svg v-show="isAgreed" class="check-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" stroke-width="3"
                        stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span>{{ $i18n('iHaveReadAndAgree', '我已阅读并同意') }}</span>
              <span class="link" @click.stop="navigateToAgreement">
                《{{ $i18n('userRechargeAgreement', '用户充值协议') }}》
              </span>
            </div>
            <button
                class="submit-btn"
                :disabled="!isAgreed || activeIndex === null || paying"
                @click="handleRecharge"
            >
              {{ $i18n('rechargeNow', '立即充值') }}{{ selectedAmount ? ' ¥' + selectedAmount : '' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 原生 loading -->
    <div class="native-loading" v-if="loadingVisible">
      <div class="loading-box">
        <div class="loading-spinner"></div>
        <span class="loading-text">{{ loadingText }}</span>
      </div>
    </div>

    <!-- 原生 toast -->
    <transition name="toast-fade">
      <div class="native-toast" v-if="toastVisible">
        <div class="toast-box">
          <span class="toast-text">{{ toastMessage }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import i18nMixin from "@/libs/i18nMixin";
import { mapGetters } from "vuex";
import wx from "weixin-js-sdk";

const AGREEMENT_URL = "https://h5.dljok.com/doc/wx-recharge.html";

export default {
  name: "RechargePopup",
  mixins: [i18nMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    purpose: {
      type: Number,
      default: 3,
    }
  },
  data() {
    return {
      rechargeList: [],
      activeIndex: null,
      selectedAmount: null,
      isAgreed: false,
      paying: false,
      loadingVisible: false,
      loadingText: "",
      toastVisible: false,
      toastMessage: "",
    };
  },
  computed: {
    ...mapGetters(["live"]),
  },
  watch: {
    show(val) {
      if (val) {
        this.getRechargeData();
      }
    },
  },
  methods: {
    close() {
      this.$emit("update:show", false);
      this.$emit("close");
    },

    showLoading(message) {
      this.loadingText = message;
      this.loadingVisible = true;
    },
    hideLoading() {
      this.loadingVisible = false;
    },

    showToast(message) {
      this.toastMessage = message;
      this.toastVisible = true;
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 1500);
    },

    getRechargeData() {
      this.showLoading(this.$i18n("loading", "加载中..."));
      this.$http
          .get("/recharge/index")
          .then((res) => {
            this.rechargeList = res.data.recharge_price_ways || [];
          })
          .catch((err) => {
            console.error("获取充值数据失败:", err);
            this.showToast(this.$i18n("dataLoadFailed", "数据加载失败"));
          })
          .finally(() => {
            this.hideLoading();
          });
    },

    selectItem(index) {
      const item = this.rechargeList[index];
      this.activeIndex = index;
      this.selectedAmount = parseFloat(item.value.price).toFixed(2);
    },

    toggleAgreement() {
      this.isAgreed = !this.isAgreed;
    },

    navigateToAgreement() {
      window.open(AGREEMENT_URL, "_blank");
    },

    handleRecharge() {
      if (this.activeIndex === null || !this.isAgreed) {
        this.showToast(this.$i18n("selectAmountAndAgree", "请选择金额并同意协议"));
        return;
      }
      const selectedItem = this.rechargeList[this.activeIndex];
      const rechargeId = selectedItem.value.id;

      this.paying = true;
      this.showLoading(this.$i18n("initiatingPayment", "发起支付中..."));

      this.$http
          .post("/api/liveSetting/createLiveRecharge", {
            rechargeId,
            liveId: this.live.id,
            purpose:this.purpose
          })
          .then((res) => {
            wx.miniProgram.navigateTo({
              url: `/pages/wxpay/wxpay?uni=${res.data}&payType=live_recharge`,
            });
          })
          .catch((err) => {
            console.error("充值失败:", err);
            this.showToast(this.$i18n("rechargeFailed", "充值失败，请重试"));
          })
          .finally(() => {
            this.hideLoading();
            this.paying = false;
          });
    },
  },
  beforeDestroy() {
    if (this._toastTimer) clearTimeout(this._toastTimer);
  },
};
</script>

<style lang="less" scoped>
/* ===== 遮罩 ===== */
.popup-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999; // 压过聊天输入框(z-index:3)和 vant 弹窗

  .popup-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 0 0 16px 16px;
    padding: 14px 14px calc(14px + env(safe-area-inset-top));
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
}

/* ===== 顶部下滑动画 ===== */
.popup-top-enter-active,
.popup-top-leave-active {
  transition: opacity 0.25s ease;

  .popup-panel {
    transition: transform 0.25s ease;
  }
}
.popup-top-enter,
.popup-top-leave-to {
  opacity: 0;

  .popup-panel {
    transform: translateY(-100%);
  }
}

/* ===== 标题栏 ===== */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .title {
    font-size: 16px;
    font-weight: bold;
    color: #333;
  }

  .close-bt {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #999;
    border-radius: 50%;
    background: #f2f2f2;
    cursor: pointer;

    &:active {
      background: #e5e5e5;
    }
  }
}

/* ===== 紧凑4列网格 ===== */
.recharge-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.recharge-item {
  background: #f8f8f8;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
  transition: all 0.15s;

  &.active {
    background: #fff5f5;
    border-color: #ff5e5e;

    .amount {
      color: #ff5e5e;
    }
  }

  .amount {
    font-size: 14px;
    font-weight: bold;
    color: #333;
  }

  .bonus {
    font-size: 10px;
    color: #ff9e2e;
    margin-top: 2px;
  }

  .tag {
    position: absolute;
    top: -6px;
    right: -4px;
    background: #ff5e5e;
    color: #fff;
    font-size: 9px;
    line-height: 1;
    padding: 2px 5px;
    border-radius: 8px;
  }
}

/* ===== 操作行：协议 + 按钮 ===== */
.action-row {
  display: flex;
  flex-direction: column;

  .agreement {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #999;
    margin-bottom: 8px;
    cursor: pointer;

    .checkbox {
      width: 15px;
      height: 15px;
      border: 1.5px solid #ccc;
      border-radius: 50%;
      margin-right: 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: #fff;
      transition: all 0.2s;

      &.checked {
        background: #ff5e5e;
        border-color: #ff5e5e;
      }

      .check-icon {
        width: 10px;
        height: 10px;
      }
    }

    .link {
      color: #576b95;
      margin-left: 2px;
    }
  }

  .submit-btn {
    width: 100%;
    height: 36px;
    line-height: 36px;
    background: linear-gradient(to right, #ff5e5e, #ff8e8e);
    color: #fff;
    border-radius: 18px;
    font-size: 14px;
    border: none;
    cursor: pointer;

    &:disabled {
      background: #ccc;
    }
  }
}

/* ===== 原生 loading ===== */
.native-loading .loading-box {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 90px;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  z-index: 10000;

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .loading-text {
    margin-top: 6px;
    font-size: 12px;
    color: #fff;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== 原生 toast ===== */
.native-toast .toast-box {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 8px;
  z-index: 10000;

  .toast-text {
    font-size: 13px;
    color: #fff;
    text-align: center;
  }
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s ease;
}
.toast-fade-enter,
.toast-fade-leave-to {
  opacity: 0;
}
</style>
